import { useState, useRef, useCallback, useEffect, type TouchEvent, type WheelEvent } from 'react';

interface PhotoViewerProps {
  src: string;
  alt: string;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  onClose?: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}

export default function PhotoViewer({
  src,
  alt,
  onNavigatePrev,
  onNavigateNext,
  onClose,
  hasPrev,
  hasNext,
}: PhotoViewerProps) {
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchStartY, setTouchStartY] = useState(0);
  const [startTranslateX, setStartTranslateX] = useState(0);
  const [startTranslateY, setStartTranslateY] = useState(0);
  const [swipeStartX, setSwipeStartX] = useState(0);
  const [swipeStartY, setSwipeStartY] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const lastDistRef = useRef<number>(0);
  const animationRef = useRef<number>(0);

  // Reset zoom when image changes
  useEffect(() => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
    setSwipeOffset(0);
    setIsSwiping(false);
  }, [src]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      } else if (e.key === 'ArrowLeft' && hasPrev && onNavigatePrev && scale === 1) {
        onNavigatePrev();
      } else if (e.key === 'ArrowRight' && hasNext && onNavigateNext && scale === 1) {
        onNavigateNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNavigatePrev, onNavigateNext, hasPrev, hasNext, scale]);

  const clampTranslate = useCallback((newScale: number, tx: number, ty: number) => {
    if (!containerRef.current) return { tx, ty };
    const rect = containerRef.current.getBoundingClientRect();
    const imgW = rect.width * newScale;
    const imgH = rect.height * newScale;
    const maxX = Math.max(0, (imgW - rect.width) / 2);
    const maxY = Math.max(0, (imgH - rect.height) / 2);
    return {
      tx: Math.max(-maxX, Math.min(maxX, tx)),
      ty: Math.max(-maxY, Math.min(maxY, ty)),
    };
  }, []);

  // Double tap to zoom
  const lastTapRef = useRef(0);
  const handleClick = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      // Double tap
      if (scale > 1) {
        setScale(1);
        setTranslateX(0);
        setTranslateY(0);
      } else {
        setScale(2.5);
      }
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  }, [scale]);

  // Touch handlers
  const getTouchDist = (touches: React.TouchList): number => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch start
      lastDistRef.current = getTouchDist(e.touches);
      setIsSwiping(false);
      setSwipeOffset(0);
    } else if (e.touches.length === 1) {
      setIsDragging(true);
      setTouchStartX(e.touches[0].clientX);
      setTouchStartY(e.touches[0].clientY);
      setStartTranslateX(translateX);
      setStartTranslateY(translateY);
      if (scale === 1) {
        setIsSwiping(true);
        setSwipeStartX(e.touches[0].clientX);
        setSwipeStartY(e.touches[0].clientY);
        setSwipeOffset(0);
      }
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 2) {
      // Pinch zoom
      const dist = getTouchDist(e.touches);
      if (lastDistRef.current > 0) {
        const newScale = Math.max(1, Math.min(5, scale * (dist / lastDistRef.current)));
        setScale(newScale);
        if (newScale <= 1) {
          setTranslateX(0);
          setTranslateY(0);
        }
      }
      lastDistRef.current = dist;
    } else if (e.touches.length === 1 && isDragging) {
      const dx = e.touches[0].clientX - touchStartX;
      const dy = e.touches[0].clientY - touchStartY;

      if (scale > 1) {
        const { tx, ty } = clampTranslate(scale, startTranslateX + dx, startTranslateY + dy);
        setTranslateX(tx);
        setTranslateY(ty);
      } else if (isSwiping) {
        const swipeDx = e.touches[0].clientX - swipeStartX;
        setSwipeOffset(swipeDx);
      }
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (e.touches.length === 0) {
      setIsDragging(false);
      lastDistRef.current = 0;

      // Handle swipe
      if (isSwiping && scale === 1 && Math.abs(swipeOffset) > 60) {
        if (swipeOffset > 0 && hasPrev && onNavigatePrev) {
          onNavigatePrev();
        } else if (swipeOffset < 0 && hasNext && onNavigateNext) {
          onNavigateNext();
        }
      }
      setIsSwiping(false);
      setSwipeOffset(0);
    }
  };

  // Mouse wheel zoom
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(1, Math.min(5, scale * delta));
    setScale(newScale);
    if (newScale <= 1) {
      setTranslateX(0);
      setTranslateY(0);
    }
  }, [scale]);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-40 bg-foreground-950 flex items-center justify-center overflow-hidden select-none"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      onClick={handleClick}
    >
      {/* Prev Button — clickable! */}
      {hasPrev && scale === 1 && !isSwiping && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigatePrev?.(); }}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-foreground-900/40 backdrop-blur-sm text-background-50 hover:bg-foreground-900/60 active:scale-95 transition-all cursor-pointer"
          aria-label="이전 사진"
        >
          <i className="ri-arrow-left-s-line text-2xl md:text-3xl"></i>
        </button>
      )}

      {/* Next Button — clickable! */}
      {hasNext && scale === 1 && !isSwiping && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigateNext?.(); }}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-foreground-900/40 backdrop-blur-sm text-background-50 hover:bg-foreground-900/60 active:scale-95 transition-all cursor-pointer"
          aria-label="다음 사진"
        >
          <i className="ri-arrow-right-s-line text-2xl md:text-3xl"></i>
        </button>
      )}

      {/* First/Last photo indicators */}
      {!hasPrev && scale === 1 && !isSwiping && (
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-background-50/30 text-xs hidden md:block">
          첫 번째 사진
        </div>
      )}
      {!hasNext && scale === 1 && !isSwiping && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-background-50/30 text-xs hidden md:block">
          마지막 사진
        </div>
      )}

      {/* Image */}
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        className="max-w-full max-h-full object-contain transition-transform duration-75"
        style={{
          transform: `translate(${translateX + (isSwiping ? swipeOffset : 0)}px, ${translateY}px) scale(${scale})`,
          opacity: isSwiping ? 0.85 : 1,
        }}
        draggable={false}
      />

      {/* Zoom indicator */}
      {scale > 1 && (
        <div className="absolute bottom-24 left-0 right-0 flex justify-center">
          <span className="bg-background-50/20 backdrop-blur-sm text-background-50/80 text-[11px] px-3 py-1 rounded-full">
            {Math.round(scale * 100)}%
          </span>
        </div>
      )}
    </div>
  );
}